import { CurrencyPipe } from '@angular/common';
import { Component, OnInit, computed, inject } from '@angular/core';
import { AccountAnalysisState } from '@core/model/domain/accountAnalysisState';
import { WaitOnBankAccountAnalysisCompletion } from '@core/usecase/waitOnBankAccountAnalysisCompletion';
import { environment } from '@environments/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AutoClosedButtonComponent } from '@shared/components/auto-closed-button/auto-closed-button.component';
import { CancelOpenBankingPopinComponent } from '@shared/components/cancel-open-banking-popin/cancel-open-banking-popin.component';
import { ProcessEventFactory } from '@shared/models/processEvents';
import { ProcessEventService } from '@shared/services/process-event.service';
import { StoreService } from '@shared/services/store.service';
import { TrackerService } from '@shared/services/tracker.service';
import { from, concatMap, delay } from 'rxjs';
import frenchi18n from '@assets/i18n/fr.json';
import { AccountsAnalysisVM } from './accounts-analysis.viewmodel';

@Component({
  selector: 'app-accounts-connection',
  templateUrl: './accounts-analysis.component.html',
  standalone: true,
  imports: [TranslateModule, CurrencyPipe, CancelOpenBankingPopinComponent, AutoClosedButtonComponent],
})
export class AccountsAnalysisComponent implements OnInit {
  private readonly storeService = inject(StoreService);
  isPopinOpen = false;
  accountAnalysisState: AccountAnalysisState | undefined;
  waitingMessage!: string;
  isAccountAnalysisDone = false;
  accountsAnalysisVM: AccountsAnalysisVM;

  constructor(
    private readonly processEventService: ProcessEventService,
    private readonly tracker: TrackerService,
    private readonly waitOnBankAccountAnalysisCompletion: WaitOnBankAccountAnalysisCompletion,
    private readonly translate: TranslateService
  ) {
    this.accountsAnalysisVM = new AccountsAnalysisVM(
      this.storeService,
      this.waitOnBankAccountAnalysisCompletion
    );
    this.waitingMessage = this.translate.instant('asking_your_bank');
  }

  async ngOnInit(): Promise<void> {
    this.accountAnalysisState = undefined;
    this.isAccountAnalysisDone = false;

    this.tracker.trackPage({
      taggingPage: 'ACCOUNT_LOADER',
      taggingPayload: {
        isMultipleAccount: this.isMultipleAccount(),
      },
    });

    await this.waitOnAccountsAnalysis();
  }

  continueSuccess = () => {
    this.tracker.trackClick({
      click: frenchi18n.continue,
      taggingPage: 'ACCOUNTS_ANALYSIS',
      taggingPayload: {
        accountAnalysisState: this.accountAnalysisState,
      },
    });
    this.processEventService.push(ProcessEventFactory.SpawnContinueEvent());
  };

  continueFailure = () => {
    this.tracker.trackClick({
      click: frenchi18n.continue,
      taggingPage: 'ACCOUNTS_ANALYSIS',
      taggingPayload: {
        accountAnalysisState: this.accountAnalysisState,
      },
    });

    this.processEventService.push(ProcessEventFactory.SpawnContinueAfterErrorEvent());
  };

  closePopin() {
    this.isPopinOpen = false;
  }

  openPopin() {
    this.tracker.trackClick({
      click: frenchi18n.come_back_to_classic_form,
      taggingPage: 'ACCOUNTS_ANALYSIS',
      taggingPayload: {
        accountAnalysisState: this.accountAnalysisState,
      },
    });

    this.isPopinOpen = true;
  }

  async waitOnAccountsAnalysis() {
    this.waitingMessage = this.translate.instant('asking_your_bank');
    this.computeWaitingMessage();
    const selectedAccountsId = this.selectedAccounts().map((account) => account.id);

    try {
      const accountAnalysisState = await this.accountsAnalysisVM.waitOnRequestAnalysis(selectedAccountsId);
      this.accountAnalysisState = accountAnalysisState;
      this.isAccountAnalysisDone = true;

      this.tracker.trackPage({
        taggingPage: 'ACCOUNTS_ANALYSIS',
        taggingPayload: {
          accountAnalysisState: this.accountAnalysisState,
        },
      });
    } catch (error) {
      this.processEventService.push(
        ProcessEventFactory.SpawnInternalErrorEvent({ error, errorOrigin: 'SelectAccountsVM - submit' })
      );
    }
  }

  retryAccountAnalysis() {
    this.trackRetryButton();
    this.processEventService.push(ProcessEventFactory.SpawnRetryAccountAnalysis());
  }

  private computeWaitingMessage() {
    from([this.translate.instant('retrieving_data'), this.translate.instant('processing')])
      .pipe(concatMap((value) => from([value]).pipe(delay(4000))))
      .subscribe((message) => {
        this.waitingMessage = message;
      });
  }

  private trackRetryButton() {
    this.tracker.trackClick({
      click: frenchi18n.retry_connection,
      taggingPage: 'ACCOUNTS_ANALYSIS',
      taggingPayload: {
        accountAnalysisState: this.accountAnalysisState,
      },
    });
  }

  readonly selectedAccounts = this.storeService.selectOrThrow('SELECTED_ACCOUNTS');
  readonly selectedBank = this.storeService.selectOrThrow('SELECTED_BANK');
  readonly isMultipleAccount = computed(() => this.selectedAccounts().length > 1);
  readonly isTestBankConnector = computed(
    () => this.selectedBank().uid === environment.testBankConnector && environment.env !== 'prod'
  );
}
